import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ProductGrid } from '../components/ProductGrid';
import { ProductFilters } from '../components/ProductFilters';
import { productsService } from '../services/products';
import { categoriesService } from '../services/categories';
import { ProductQueryParams } from '../types/woocommerce';
import { SparklesIcon, FireIcon } from '@heroicons/react/24/outline';

export const SalePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<ProductQueryParams>(() => {
    const params: ProductQueryParams = {
      page: parseInt(searchParams.get('page') || '1'),
      per_page: 12,
      on_sale: true, // Toujours filtrer sur les produits en promotion
    };

    // Récupérer les autres filtres depuis l'URL
    if (searchParams.get('orderby')) params.orderby = searchParams.get('orderby')!;
    if (searchParams.get('order')) params.order = searchParams.get('order') as 'asc' | 'desc';
    if (searchParams.get('category')) params.category = searchParams.get('category')!;
    if (searchParams.get('min_price')) params.min_price = searchParams.get('min_price')!;
    if (searchParams.get('max_price')) params.max_price = searchParams.get('max_price')!;
    if (searchParams.get('featured')) params.featured = searchParams.get('featured') === 'true';
    if (searchParams.get('stock_status')) params.stock_status = searchParams.get('stock_status') as any;

    return params;
  });

  // Récupérer les produits en promotion
  const { data: productsData, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ['products', 'sale', filters],
    queryFn: () => productsService.getOnSaleProducts(filters),
  });

  // Récupérer les catégories pour les filtres
  const { data: categoriesData } = useQuery({
    queryKey: ['categories', 'all'],
    queryFn: () => categoriesService.getCategories({ per_page: 100, hide_empty: true }),
  });

  // Calculer les statistiques des promotions
  const promotionStats = productsData?.products.reduce((acc, product) => {
    const discount = productsService.getDiscountPercentage(product.regular_price, product.sale_price);
    if (discount > acc.maxDiscount) {
      acc.maxDiscount = discount;
    }
    acc.totalSavings += (parseFloat(product.regular_price) - parseFloat(product.sale_price)) * 1;
    return acc;
  }, { maxDiscount: 0, totalSavings: 0 }) || { maxDiscount: 0, totalSavings: 0 };

  // Mettre à jour l'URL quand les filtres changent
  useEffect(() => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && key !== 'on_sale') {
        params.set(key, value.toString());
      }
    });

    setSearchParams(params);
  }, [filters, setSearchParams]);

  const handleFiltersChange = (newFilters: ProductQueryParams) => {
    // Toujours garder on_sale à true
    setFilters({ ...newFilters, on_sale: true, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page });
    window.scrollTo(0, 0);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Bannière promotionnelle */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container-custom py-12">
          <div className="text-center">
            <div className="flex justify-center items-center gap-3 mb-4">
              <FireIcon className="h-10 w-10" />
              <h1 className="text-4xl font-bold">Promotions en cours</h1>
              <FireIcon className="h-10 w-10" />
            </div>
            <p className="text-xl mb-6">
              Profitez de nos offres exceptionnelles !
            </p>
            
            {productsData && productsData.products.length > 0 && (
              <div className="flex flex-wrap justify-center gap-6 mt-8">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-4">
                  <p className="text-3xl font-bold">{productsData.pagination.total}</p>
                  <p className="text-sm">Produits en promotion</p>
                </div>
                {promotionStats.maxDiscount > 0 && (
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-4">
                    <p className="text-3xl font-bold">-{promotionStats.maxDiscount}%</p>
                    <p className="text-sm">Réduction maximale</p>
                  </div>
                )}
                {promotionStats.totalSavings > 0 && (
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-4">
                    <p className="text-3xl font-bold">
                      {productsService.getFormattedPrice(promotionStats.totalSavings)}
                    </p>
                    <p className="text-sm">D'économies potentielles</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Badges promotionnels */}
      <div className="bg-red-50 border-b border-red-100">
        <div className="container-custom py-4">
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
            <div className="flex items-center gap-2 text-red-700">
              <SparklesIcon className="h-5 w-5" />
              <span>Livraison gratuite dès 50€</span>
            </div>
            <div className="flex items-center gap-2 text-red-700">
              <SparklesIcon className="h-5 w-5" />
              <span>Retours gratuits</span>
            </div>
            <div className="flex items-center gap-2 text-red-700">
              <SparklesIcon className="h-5 w-5" />
              <span>Paiement sécurisé</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        <div className="lg:grid lg:grid-cols-4 lg:gap-8">
          {/* Filtres */}
          <aside className="mb-8 lg:mb-0">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-yellow-800 mb-2">
                💡 Astuce
              </h3>
              <p className="text-sm text-yellow-700">
                Les promotions sont limitées dans le temps. N'attendez pas pour en profiter !
              </p>
            </div>
            <ProductFilters
              categories={categoriesData?.categories || []}
              currentFilters={filters}
              onFiltersChange={handleFiltersChange}
            />
          </aside>

          {/* Grille de produits */}
          <main className="lg:col-span-3">
            {/* Résumé des résultats */}
            {productsData && (
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-gray-700">
                  Affichage de{' '}
                  <span className="font-medium">
                    {(filters.page! - 1) * filters.per_page! + 1}
                  </span>{' '}
                  à{' '}
                  <span className="font-medium">
                    {Math.min(filters.page! * filters.per_page!, productsData.pagination.total)}
                  </span>{' '}
                  sur{' '}
                  <span className="font-medium">{productsData.pagination.total}</span>{' '}
                  produits en promotion
                </p>
              </div>
            )}

            <ProductGrid
              products={productsData?.products || []}
              loading={productsLoading}
              error={productsError as Error}
              emptyMessage="Aucun produit en promotion pour le moment"
            />

            {/* Pagination */}
            {productsData && productsData.pagination.totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <nav className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(filters.page! - 1)}
                    disabled={filters.page === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>

                  {/* Numéros de page */}
                  <div className="flex gap-1">
                    {[...Array(Math.min(5, productsData.pagination.totalPages))].map((_, idx) => {
                      let pageNum: number;
                      if (productsData.pagination.totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (filters.page! <= 3) {
                        pageNum = idx + 1;
                      } else if (filters.page! >= productsData.pagination.totalPages - 2) {
                        pageNum = productsData.pagination.totalPages - 4 + idx;
                      } else {
                        pageNum = filters.page! - 2 + idx;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            pageNum === filters.page
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(filters.page! + 1)}
                    disabled={filters.page === productsData.pagination.totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </nav>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

